// apps/web/src/components/integrations/IntegrationManager.tsx
// Integration management dashboard

import type React from 'react';
import { useState, useEffect } from 'react';
import type { Integration } from '../../types/integration';
import { apiClient } from '../../lib/apiClient';

export function IntegrationManager() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showConnectForm, setShowConnectForm] = useState(false);

  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/v1/integrations');
        setIntegrations(response.data.integrations);
      } catch (err) {
        setError('Failed to load integrations');
        console.error('Error loading integrations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, []);

  const handleConnectIntegration = async (integrationData: any) => {
    try {
      const response = await apiClient.post('/api/v1/integrations', integrationData);
      setIntegrations([response.data, ...integrations]);
      setShowConnectForm(false);
    } catch (err) {
      setError('Failed to connect integration');
      console.error('Error connecting integration:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading integrations...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  return (
    <div className="integration-manager">
      <div className="header">
        <h2>Integrations</h2>
        <button 
          onClick={() => setShowConnectForm(!showConnectForm)}
          className="btn btn-primary"
        >
          {showConnectForm ? 'Cancel' : 'Connect Integration'}
        </button>
      </div>

      {showConnectForm && (
        <IntegrationConnectForm 
          onSubmit={handleConnectIntegration} 
          onCancel={() => setShowConnectForm(false)} 
        />
      )}

      <div className="integrations-grid">
        {integrations.map(integration => (
          <IntegrationCard key={integration.id} integration={integration} />
        ))}
      </div>
    </div>
  );
}

// Helper component
function IntegrationCard({ integration }: { integration: Integration }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONNECTED': return 'status-connected';
      case 'DISCONNECTED': return 'status-disconnected';
      case 'ERROR': return 'status-error';
      default: return 'status-default';
    }
  };

  return (
    <div className="integration-card">
      <div className="integration-header">
        <h3>{integration.name}</h3>
        <span className={`status-badge ${getStatusColor(integration.status)}`}>
          {integration.status}
        </span>
      </div>
      <p><strong>Type:</strong> {integration.type}</p>
      <p><strong>Connected:</strong> {integration.connectedAt ? new Date(integration.connectedAt).toLocaleDateString() : 'Never'}</p>
      <p><strong>Description:</strong> {integration.description || 'No description'}</p>
      <div className="actions">
        <button className="btn btn-secondary">Configure</button>
        {integration.status === 'CONNECTED' && (
          <button className="btn btn-danger">Disconnect</button>
        )}
        {integration.status === 'DISCONNECTED' && (
          <button className="btn btn-primary">Connect</button>
        )}
      </div>
    </div>
  );
}

// Helper component
function IntegrationConnectForm({ onSubmit, onCancel }: { onSubmit: any; onCancel: any }) {
  const [formData, setFormData] = useState({
    name: '',
    type: 'SOCIAL_MEDIA',
    description: '',
    config: {}
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="integration-connect-form">
      <h3>Connect New Integration</h3>
      <input
        type="text"
        placeholder="Integration Name"
        value={formData.name}
        onChange={(e) => setFormData({...formData, name: e.target.value})}
        required
      />
      <select
        value={formData.type}
        onChange={(e) => setFormData({...formData, type: e.target.value})}
      >
        <option value="SOCIAL_MEDIA">Social Media</option>
        <option value="EMAIL">Email</option>
        <option value="PAYMENT">Payment</option>
        <option value="ANALYTICS">Analytics</option>
        <option value="CRM">CRM</option>
        <option value="OTHER">Other</option>
      </select>
      <textarea
        placeholder="Description"
        value={formData.description}
        onChange={(e) => setFormData({...formData, description: e.target.value})}
      />
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">Connect</button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">Cancel</button>
      </div>
    </form>
  );
}